import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { backendURL } from "../config";
import {
    Shield, Layers, Activity, Search, Filter, ArrowRight,
    CheckCircle2, Clock, Bot, Sparkles, ExternalLink,
    Plus, ChevronRight, Check, AlertTriangle, ShieldCheck,
    Cpu, Globe, Terminal, Play, Zap, RefreshCw, X, Radio,
    Server, Database, Network, Lock, SlidersHorizontal, ToggleLeft, ToggleRight
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function DetectionIntelligence() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("coverage"); // coverage | escalation | routing
    const [timeRange, setTimeRange] = useState("Last week");
    const [searchQuery, setSearchQuery] = useState("");
    const [intelData, setIntelData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedSource, setSelectedSource] = useState(null);
    const [newRuleModalOpen, setNewRuleModalOpen] = useState(false);
    const [toast, setToast] = useState(null);

    const [newRuleForm, setNewRuleForm] = useState({
        name: "",
        connector: "FortiGate Perimeter Firewall",
        trigger: "",
        targetAgent: "QueryTel Autonomous Triage",
        action: "Auto-Investigate"
    });

    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    const showToast = (message, type = "success") => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3500);
    };

    const fetchIntelData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(
                `${backendURL}/api/detect/intelligence?timeRange=${encodeURIComponent(timeRange)}`,
                { headers }
            );
            setIntelData(res.data);
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem("token");
                window.location.href = "/login";
                return;
            }
            console.error("Failed to load detection intelligence:", err);
            showToast("Failed to fetch detection intelligence", "error");
        } finally {
            setLoading(false);
        }
    }, [timeRange]);

    useEffect(() => {
        fetchIntelData();
    }, [fetchIntelData]);

    const handleCreateRule = async (e) => {
        e.preventDefault();
        try {
            const res = await axios.post(`${backendURL}/api/detect/intelligence/routing-rule`, newRuleForm, { headers });
            if (res.data.success) {
                setIntelData(prev => ({
                    ...prev,
                    routingRules: [...(prev?.routingRules || []), res.data.rule]
                }));
                setNewRuleModalOpen(false);
                setNewRuleForm({
                    name: "",
                    connector: "FortiGate Perimeter Firewall",
                    trigger: "",
                    targetAgent: "QueryTel Autonomous Triage",
                    action: "Auto-Investigate"
                });
                showToast("✓ Detection routing rule deployed to active pipeline!", "success");
            }
        } catch (err) {
            console.error("Failed to create routing rule:", err);
            showToast("Failed to create routing rule", "error");
        }
    };

    const handleToggleRule = async (ruleId) => {
        try {
            const res = await axios.patch(`${backendURL}/api/detect/intelligence/routing-rule/${ruleId}/toggle`, {}, { headers });
            if (res.data.success) {
                setIntelData(prev => ({
                    ...prev,
                    routingRules: (prev?.routingRules || []).map(r => r.id === ruleId ? { ...r, enabled: res.data.enabled } : r)
                }));
                showToast(`Rule ${res.data.enabled ? 'Enabled' : 'Disabled'}`, "success");
            }
        } catch (err) {
            console.error("Failed to toggle rule:", err);
        }
    };

    const sources = intelData?.sources || [];
    const filteredSources = sources.filter(s =>
        s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.categories.some(c => c.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const spectrum = intelData?.coverageSpectrum || [];

    // Helper for Spectrum Tile Colors
    const getSpectrumBadgeStyle = (status) => {
        switch (status) {
            case "Full":
                return "bg-blue-600/90 text-white border-blue-400/40 shadow-sm";
            case "Majority":
                return "bg-indigo-600/80 text-indigo-100 border-indigo-500/30";
            case "Enriched":
                return "bg-indigo-900/90 text-indigo-200 border-indigo-700/40";
            case "Partial":
                return "bg-slate-800 text-slate-300 border-white/10";
            case "None":
            default:
                return "bg-black/80 text-slate-500 border-white/[0.04]";
        }
    };

    const totalAlerts = intelData?.totalAlerts || 0;
    const investigatedRate = intelData?.investigatedPercent ?? 100.0;

    return (
        <div className="w-full space-y-6 pb-14 font-sans selection:bg-blue-500/30 text-slate-200">
            {/* ─── Toast Feedback ─── */}
            <AnimatePresence>
                {toast && (
                    <motion.div
                        initial={{ opacity: 0, y: -20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -20, scale: 0.95 }}
                        className={`fixed top-6 right-8 z-50 flex items-center gap-3 px-5 py-3 rounded-2xl shadow-2xl border backdrop-blur-xl ${
                            toast.type === "success"
                                ? "bg-emerald-950/90 border-emerald-500/40 text-emerald-200"
                                : "bg-rose-950/90 border-rose-500/40 text-rose-200"
                        }`}
                    >
                        {toast.type === "success" ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <AlertTriangle className="w-5 h-5 text-rose-400" />}
                        <span className="text-xs font-semibold">{toast.message}</span>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ─── Top Breadcrumb & Live Sync ─── */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[11px] font-mono text-slate-500 uppercase tracking-wider">
                    <span>01</span>
                    <span>/</span>
                    <span className="text-slate-400 font-bold">Detection Intelligence</span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-mono font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping"></span>
                        LIVE DATABASE SYNC
                    </span>
                    <button
                        onClick={fetchIntelData}
                        disabled={loading}
                        className="p-1.5 rounded-lg bg-white/[0.05] hover:bg-white/[0.1] text-slate-400 hover:text-white transition-all"
                        title="Refresh Detection Intelligence"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {/* ─── Main Hero Layout (2 Columns) ─── */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                
                {/* ═══ LEFT HERO DESCRIPTION (5 cols) ═══ */}
                <div className="lg:col-span-5 space-y-6">
                    <div>
                        <h1 className="text-[28px] lg:text-[34px] font-black text-white tracking-tight leading-tight">
                            Every alert, every source, one view.
                        </h1>
                        <p className="text-[13.5px] text-slate-300 mt-3 leading-relaxed">
                            Connect the detection tools you already run across <strong>identity, endpoint, cloud, email, and FortiOS perimeter SIEM</strong>, and see your entire alert inventory in one place. QueryTel shows what each source is producing and how much of it has been investigated to a conclusion.
                        </p>
                    </div>

                    <div className="space-y-2.5 pt-2">
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>Every source connected and normalized into one inventory.</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>Investigation coverage shown for each source.</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>Nothing sits unseen in a queue.</span>
                        </div>
                    </div>

                    {/* Quick Stats Summary Card */}
                    <div className="bg-[#111827] border border-white/[0.08] rounded-2xl p-5 shadow-xl space-y-4">
                        <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                            <span className="text-[12px] font-bold text-white uppercase tracking-wider">Autonomous Triage Ratio</span>
                            <span className="text-[11px] font-mono text-emerald-400 bg-emerald-500/10 px-2.5 py-0.5 rounded-full border border-emerald-500/20 font-bold">
                                Active Fleet
                            </span>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="p-3.5 bg-black/40 rounded-xl border border-white/[0.04]">
                                <span className="text-[10px] text-slate-400 font-mono uppercase block">Total Ingested</span>
                                <span className="text-[22px] font-black text-white font-mono">
                                    {loading ? (
                                        <span className="text-slate-500 text-sm animate-pulse">Calculating...</span>
                                    ) : (
                                        totalAlerts.toLocaleString()
                                    )}
                                </span>
                            </div>
                            <div className="p-3.5 bg-black/40 rounded-xl border border-white/[0.04]">
                                <span className="text-[10px] text-slate-400 font-mono uppercase block">Investigated Rate</span>
                                <span className="text-[22px] font-black text-emerald-400 font-mono">
                                    {loading ? (
                                        <span className="text-slate-500 text-sm animate-pulse">...</span>
                                    ) : (
                                        `${investigatedRate}%`
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Quick Action Navigation */}
                    <div className="flex items-center gap-3 pt-2">
                        <button
                            onClick={() => navigate("/incidents")}
                            className="flex-1 py-3 px-4 rounded-xl bg-blue-600/10 hover:bg-blue-600/20 text-blue-400 border border-blue-500/20 font-mono text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2 transition-all shadow-sm"
                        >
                            <Shield className="w-4 h-4" />
                            View Incidents Queue ({intelData?.totalIncidents?.toLocaleString() || '11,224'})
                        </button>
                        <button
                            onClick={() => navigate("/firewall-approvals")}
                            className="flex-1 py-3 px-4 rounded-xl bg-emerald-600/10 hover:bg-emerald-600/20 text-emerald-400 border border-emerald-500/20 font-mono text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2 transition-all shadow-sm"
                        >
                            <ShieldCheck className="w-4 h-4" />
                            Firewall Approvals
                        </button>
                    </div>
                </div>

                {/* ═══ RIGHT WORKBENCH: Interactive QueryTel Canvas (7 cols) ═══ */}
                <div className="lg:col-span-7 bg-[#111827] border border-white/[0.08] rounded-2xl p-6 shadow-2xl space-y-6">
                    
                    {/* Top Tabs */}
                    <div className="flex items-center gap-6 border-b border-white/[0.06] pb-3">
                        <button
                            onClick={() => setActiveTab("coverage")}
                            className={`text-[13px] font-bold pb-2 transition-all relative ${
                                activeTab === "coverage"
                                    ? "text-blue-400"
                                    : "text-slate-400 hover:text-slate-200"
                            }`}
                        >
                            Investigation Coverage
                            {activeTab === "coverage" && (
                                <span className="absolute bottom-[-13px] left-0 right-0 h-0.5 bg-blue-500 rounded-full" />
                            )}
                        </button>

                        <button
                            onClick={() => setActiveTab("escalation")}
                            className={`text-[13px] font-bold pb-2 transition-all relative ${
                                activeTab === "escalation"
                                    ? "text-blue-400"
                                    : "text-slate-400 hover:text-slate-200"
                            }`}
                        >
                            Escalation
                            {activeTab === "escalation" && (
                                <span className="absolute bottom-[-13px] left-0 right-0 h-0.5 bg-blue-500 rounded-full" />
                            )}
                        </button>

                        <button
                            onClick={() => setActiveTab("routing")}
                            className={`text-[13px] font-bold pb-2 transition-all relative ${
                                activeTab === "routing"
                                    ? "text-blue-400"
                                    : "text-slate-400 hover:text-slate-200"
                            }`}
                        >
                            Detection Routing
                            {activeTab === "routing" && (
                                <span className="absolute bottom-[-13px] left-0 right-0 h-0.5 bg-blue-500 rounded-full" />
                            )}
                        </button>
                    </div>

                    {/* Filter & Action Controls */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        {/* Search Input */}
                        <div className="flex items-center bg-black/40 border border-white/[0.08] rounded-xl px-3 py-2 flex-1 focus-within:border-blue-500/40 transition-colors">
                            <Search className="w-3.5 h-3.5 text-slate-500 mr-2" />
                            <input
                                type="text"
                                placeholder="Search detection sources or categories..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="bg-transparent border-none outline-none text-[12px] text-white placeholder:text-slate-600 w-full"
                            />
                        </div>

                        {/* Controls */}
                        <div className="flex items-center gap-2 self-start sm:self-auto">
                            <select
                                value={timeRange}
                                onChange={(e) => setTimeRange(e.target.value)}
                                className="bg-[#1E293B] text-slate-200 border border-white/[0.12] text-[11px] font-medium rounded-xl px-3 py-2 outline-none cursor-pointer"
                            >
                                <option value="Last 24 hours" className="bg-[#0F172A]">Last 24 hours</option>
                                <option value="Last week" className="bg-[#0F172A]">Last week</option>
                                <option value="Last 30 days" className="bg-[#0F172A]">Last 30 days</option>
                            </select>

                            <button
                                onClick={() => navigate("/investigations")}
                                className="px-3.5 py-2 bg-blue-600/10 hover:bg-blue-600/20 text-blue-400 border border-blue-500/20 rounded-xl text-[11px] font-bold transition-all whitespace-nowrap flex items-center gap-1"
                            >
                                View in Investigations <ExternalLink className="w-3 h-3" />
                            </button>
                        </div>
                    </div>

                    {/* ═══ TAB 1: INVESTIGATION COVERAGE ═══ */}
                    {activeTab === "coverage" && (
                        <div className="space-y-6 animate-in fade-in duration-200">
                            
                            {/* All Alerts Master Bar */}
                            <div className="p-4 bg-black/40 border border-white/[0.06] rounded-xl space-y-2">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="text-[14px] font-black text-white">All Active Ingested Telemetry</h3>
                                        <span className="text-[11px] text-slate-400 font-mono">Normalized Fleet Inventory</span>
                                    </div>
                                    <div className="text-right">
                                        <span className="text-[13px] font-bold text-white font-mono">100%</span>
                                        <span className="text-[11px] text-slate-400 block font-mono">
                                            {loading ? "..." : `${totalAlerts.toLocaleString()} Events`}
                                        </span>
                                    </div>
                                </div>
                                <div className="w-full h-2 bg-black/60 rounded-full overflow-hidden">
                                    <div className="h-full bg-blue-500 rounded-full w-full" />
                                </div>
                            </div>

                            {/* Source Breakdown List */}
                            <div className="space-y-3">
                                {filteredSources.map((src) => {
                                    const isSelected = selectedSource?.id === src.id;
                                    return (
                                        <div
                                            key={src.id}
                                            onClick={() => setSelectedSource(isSelected ? null : src)}
                                            className={`p-3.5 rounded-xl border transition-all cursor-pointer ${
                                                isSelected
                                                    ? "bg-white/[0.06] border-blue-500/50 shadow-lg"
                                                    : "bg-black/20 border-white/[0.04] hover:bg-white/[0.02] hover:border-white/[0.08]"
                                            }`}
                                        >
                                            <div className="flex items-center justify-between gap-4">
                                                {/* Left: Badge + Name + Category Tags */}
                                                <div className="flex items-center gap-3">
                                                    {/* Tool Logo Badge */}
                                                    <div
                                                        className="w-9 h-9 rounded-lg flex items-center justify-center font-black text-[10.5px] font-mono text-white shadow-sm flex-shrink-0"
                                                        style={{ backgroundColor: src.color }}
                                                    >
                                                        {src.badge}
                                                    </div>

                                                    <div>
                                                        <h4 className="text-[13px] font-bold text-white">{src.name}</h4>
                                                        <div className="flex items-center gap-1 mt-1 overflow-x-auto">
                                                            {src.categories.slice(0, 3).map((cat, cIdx) => (
                                                                <span
                                                                    key={cIdx}
                                                                    className="text-[9px] font-mono uppercase bg-blue-500/10 text-blue-300 px-1.5 py-0.5 rounded font-semibold border border-blue-500/20"
                                                                >
                                                                    {cat}
                                                                </span>
                                                            ))}
                                                            {src.categories.length > 3 && (
                                                                <span className="text-[9px] font-mono text-slate-500 px-1">
                                                                    +{src.categories.length - 3}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Right: Bar + Percentage + Counts */}
                                                <div className="flex items-center gap-4 flex-shrink-0">
                                                    {/* Proportional Progress Bar */}
                                                    <div className="hidden sm:block w-32 h-1.5 bg-black/60 rounded-full overflow-hidden">
                                                        <div
                                                            className="h-full rounded-full transition-all duration-500"
                                                            style={{
                                                                width: `${Math.min(src.percent * 2.8, 100)}%`,
                                                                backgroundColor: src.color
                                                            }}
                                                        />
                                                    </div>

                                                    <div className="text-right min-w-[70px]">
                                                        <span className="text-[13px] font-bold text-white font-mono">
                                                            {src.percent}%
                                                        </span>
                                                        <span className="text-[10.5px] text-slate-400 block font-mono">
                                                            {src.count.toLocaleString()} Events
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Expanded Drilldown Info on Click */}
                                            {isSelected && (
                                                <div className="mt-3 pt-3 border-t border-white/[0.06] grid grid-cols-3 gap-2 text-[11px] animate-in fade-in duration-150">
                                                    <div className="p-2.5 bg-black/40 rounded-lg">
                                                        <span className="text-slate-400 block text-[10px]">Investigated</span>
                                                        <span className="font-bold text-emerald-400 font-mono text-xs">{src.investigatedPercent}%</span>
                                                    </div>
                                                    <div className="p-2.5 bg-black/40 rounded-lg">
                                                        <span className="text-slate-400 block text-[10px]">Escalation Ratio</span>
                                                        <span className="font-bold text-amber-400 font-mono text-xs">{src.escalationRate}%</span>
                                                    </div>
                                                    <div className="p-2.5 bg-black/40 rounded-lg">
                                                        <span className="text-slate-400 block text-[10px]">Connector Status</span>
                                                        <span className="font-bold text-blue-400 font-mono text-xs uppercase flex items-center gap-1">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse"></span>
                                                            Connected
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Bottom Coverage Spectrum Matrix */}
                            <div className="pt-4 border-t border-white/[0.06] space-y-3">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
                                        <Layers className="w-3.5 h-3.5 text-blue-400" />
                                        Investigation Depth Spectrum
                                    </span>
                                    {/* Color Legend */}
                                    <div className="flex items-center gap-3 text-[10px] text-slate-400 font-mono">
                                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" /> Full</span>
                                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-indigo-500" /> Majority</span>
                                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-indigo-900" /> Enriched</span>
                                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-700" /> Partial</span>
                                    </div>
                                </div>

                                {/* Spectrum Tiles Grid */}
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                                    {spectrum.map((tile, tIdx) => (
                                        <div
                                            key={tIdx}
                                            className={`p-2.5 rounded-xl border flex flex-col justify-between min-h-[64px] transition-all hover:scale-105 cursor-pointer ${getSpectrumBadgeStyle(tile.status)}`}
                                            title={`${tile.title} (${tile.source}): ${tile.status} Coverage`}
                                        >
                                            <span className="text-[10px] font-bold leading-tight line-clamp-2">
                                                {tile.title}
                                            </span>
                                            <div className="flex items-center justify-between text-[8.5px] font-mono opacity-80 mt-1">
                                                <span>{tile.source}</span>
                                                <span className="font-bold uppercase">{tile.status}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                        </div>
                    )}

                    {/* ═══ TAB 2: ESCALATION METRICS ═══ */}
                    {activeTab === "escalation" && (
                        <div className="space-y-6 animate-in fade-in duration-200">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div className="p-4 bg-black/40 border border-white/[0.06] rounded-xl space-y-1">
                                    <span className="text-[10px] text-slate-400 font-mono uppercase block">Auto-Contained / Drops</span>
                                    <span className="text-[22px] font-black text-emerald-400 font-mono">
                                        {intelData?.escalationMetrics?.autoContainedPercent ?? 0}%
                                    </span>
                                    <span className="text-[10px] text-slate-400 block font-mono">
                                        {(intelData?.escalationMetrics?.autoContained ?? 0).toLocaleString()} sessions dropped/mitigated
                                    </span>
                                </div>

                                <div className="p-4 bg-black/40 border border-white/[0.06] rounded-xl space-y-1">
                                    <span className="text-[10px] text-slate-400 font-mono uppercase block">Suppressed / Benign Sessions</span>
                                    <span className="text-[22px] font-black text-blue-400 font-mono">
                                        {intelData?.escalationMetrics?.falsePositivePercent ?? 0}%
                                    </span>
                                    <span className="text-[10px] text-slate-400 block font-mono">
                                        {(intelData?.escalationMetrics?.falsePositiveFiltered ?? 0).toLocaleString()} sessions auto-analyzed
                                    </span>
                                </div>

                                <div className="p-4 bg-black/40 border border-white/[0.06] rounded-xl space-y-1">
                                    <span className="text-[10px] text-slate-400 font-mono uppercase block">Promoted to Incident</span>
                                    <span className="text-[22px] font-black text-rose-400 font-mono">
                                        {intelData?.escalationMetrics?.escalationPercent ?? 0}%
                                    </span>
                                    <span className="text-[10px] text-slate-400 block font-mono">
                                        {(intelData?.escalationMetrics?.totalEscalated ?? 0).toLocaleString()} true positive incidents
                                    </span>
                                </div>
                            </div>

                            {/* Source Escalation Breakdown Table */}
                            <div className="border border-white/[0.06] rounded-xl overflow-hidden bg-black/30">
                                <div className="p-3.5 border-b border-white/[0.06] bg-black/50 text-[12px] font-bold text-white flex justify-between items-center">
                                    <span>Source Escalation & Auto-Resolution Breakdown</span>
                                    <span className="text-[10px] font-mono text-slate-400">Mean Time to Triage: ~38s</span>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left text-[11.5px]">
                                        <thead className="border-b border-white/[0.04] bg-white/[0.02] text-[10px] uppercase font-mono text-slate-400">
                                            <tr>
                                                <th className="p-3">Source Name</th>
                                                <th className="p-3">Escalated / Incidents</th>
                                                <th className="p-3">Auto-Closed / Suppressed</th>
                                                <th className="p-3">Avg MTTI</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-white/[0.04]">
                                            {(intelData?.escalationMetrics?.sourcesBreakdown || []).map((row, idx) => (
                                                <tr key={idx} className="hover:bg-white/[0.02]">
                                                    <td className="p-3 font-semibold text-slate-200">{row.name}</td>
                                                    <td className="p-3 text-rose-400 font-mono font-bold">{row.escalated?.toLocaleString()} events</td>
                                                    <td className="p-3 text-emerald-400 font-mono">{row.closedFalsePositive?.toLocaleString()} events</td>
                                                    <td className="p-3 text-slate-400 font-mono">38s</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ═══ TAB 3: DETECTION ROUTING ═══ */}
                    {activeTab === "routing" && (
                        <div className="space-y-4 animate-in fade-in duration-200">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                <div>
                                    <h4 className="text-[14px] font-bold text-white">Autonomous Routing Pipeline</h4>
                                    <p className="text-[11.5px] text-slate-400">Define how normalized telemetry streams feed directly into QueryTel investigation sub-agents.</p>
                                </div>
                                <button
                                    onClick={() => setNewRuleModalOpen(true)}
                                    className="flex items-center gap-1.5 px-3.5 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-[11px] font-bold shadow-md transition-all self-start sm:self-auto"
                                >
                                    <Plus className="w-3.5 h-3.5" />
                                    Add Routing Rule
                                </button>
                            </div>

                            <div className="space-y-2.5">
                                {(intelData?.routingRules || []).map((rule) => (
                                    <div key={rule.id} className="p-4 bg-black/40 border border-white/[0.06] rounded-xl flex flex-col md:flex-row md:items-center justify-between gap-3">
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-2">
                                                <h5 className="text-[12.5px] font-bold text-white">{rule.name}</h5>
                                                <button
                                                    onClick={() => handleToggleRule(rule.id)}
                                                    className={`text-[9.5px] font-mono px-2 py-0.5 rounded font-bold border transition-all ${
                                                        rule.enabled !== false
                                                            ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
                                                            : "bg-slate-700/40 text-slate-400 border-slate-600/40"
                                                    }`}
                                                >
                                                    {rule.enabled !== false ? "ACTIVE" : "DISABLED"}
                                                </button>
                                            </div>
                                            <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-400 font-mono">
                                                <span className="text-slate-300 font-semibold">{rule.connector}</span>
                                                <span>→</span>
                                                <span className="text-blue-400 font-bold">{rule.trigger}</span>
                                                <span>→</span>
                                                <span className="text-purple-400 font-bold">{rule.targetAgent}</span>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <span className="text-[11px] text-slate-300 bg-white/[0.04] px-2.5 py-1 rounded border border-white/[0.06] whitespace-nowrap self-start md:self-auto font-mono">
                                                {rule.action}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                </div>

            </div>

            {/* ─── Add Routing Rule Modal ─── */}
            {newRuleModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-[#0F172A] border border-white/[0.1] rounded-2xl w-full max-w-lg p-6 space-y-4 shadow-2xl">
                        <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                            <h3 className="text-[15px] font-bold text-white">Create Detection Routing Rule</h3>
                            <button onClick={() => setNewRuleModalOpen(false)} className="text-slate-400 hover:text-white">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <form onSubmit={handleCreateRule} className="space-y-3.5 text-[12px]">
                            <div>
                                <label className="text-slate-400 block mb-1">Rule Name</label>
                                <input
                                    type="text"
                                    required
                                    placeholder="e.g. FortiGate Inbound Port Scan Ingestion"
                                    value={newRuleForm.name}
                                    onChange={(e) => setNewRuleForm({ ...newRuleForm, name: e.target.value })}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-3 py-2 text-white outline-none focus:border-blue-500 font-mono text-xs"
                                />
                            </div>

                            <div>
                                <label className="text-slate-400 block mb-1">Connector Source</label>
                                <select
                                    value={newRuleForm.connector}
                                    onChange={(e) => setNewRuleForm({ ...newRuleForm, connector: e.target.value })}
                                    className="w-full bg-[#1E293B] border border-white/[0.12] rounded-xl px-3 py-2 text-white outline-none font-mono text-xs"
                                >
                                    <option value="Microsoft Defender XDR">Microsoft Defender XDR</option>
                                    <option value="ThreatDown (Malwarebytes) EDR">ThreatDown (Malwarebytes) EDR</option>
                                    <option value="FortiGate Perimeter Firewall">FortiGate Perimeter Firewall</option>
                                    <option value="FortiGate SSL-VPN">FortiGate SSL-VPN</option>
                                    <option value="FortiGuard IPS / Antivirus">FortiGuard IPS / Antivirus</option>
                                    <option value="FortiGate DNS Security">FortiGate DNS Security</option>
                                    <option value="QueryTel Autonomous Triage Engine">QueryTel Autonomous Triage Engine</option>
                                </select>
                            </div>

                            <div>
                                <label className="text-slate-400 block mb-1">Trigger Filter</label>
                                <input
                                    type="text"
                                    required
                                    placeholder="e.g. Action.Deny >= 50 OR Severity == 'Critical'"
                                    value={newRuleForm.trigger}
                                    onChange={(e) => setNewRuleForm({ ...newRuleForm, trigger: e.target.value })}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-3 py-2 text-white outline-none focus:border-blue-500 font-mono text-xs"
                                />
                            </div>

                            <div>
                                <label className="text-slate-400 block mb-1">Target QueryTel Agent</label>
                                <select
                                    value={newRuleForm.targetAgent}
                                    onChange={(e) => setNewRuleForm({ ...newRuleForm, targetAgent: e.target.value })}
                                    className="w-full bg-[#1E293B] border border-white/[0.12] rounded-xl px-3 py-2 text-white outline-none font-mono text-xs"
                                >
                                    <option value="QueryTel Autonomous Triage">QueryTel Autonomous Triage</option>
                                    <option value="QueryTel Perimeter Defense Agent">QueryTel Perimeter Defense Agent</option>
                                    <option value="QueryTel Identity & Access Correlator">QueryTel Identity & Access Correlator</option>
                                    <option value="QueryTel Host Telemetry Agent">QueryTel Host Telemetry Agent</option>
                                    <option value="QueryTel Network Threat Hunter">QueryTel Network Threat Hunter</option>
                                </select>
                            </div>

                            <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/[0.06]">
                                <button
                                    type="button"
                                    onClick={() => setNewRuleModalOpen(false)}
                                    className="px-4 py-2 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 rounded-xl font-mono text-xs"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-mono text-xs font-bold shadow-md"
                                >
                                    Save Rule
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
